const API_BASE = '';

function getAuthToken(): string | null {
  try {
    const config = JSON.parse(localStorage.getItem('olu_config') || '{}');
    return config.token || null;
  } catch {
    return null;
  }
}

export function setAuthToken(token: string) {
  const config = JSON.parse(localStorage.getItem('olu_config') || '{}');
  config.token = token;
  localStorage.setItem('olu_config', JSON.stringify(config));
}

async function fetchJSON<T>(url: string, options?: RequestInit): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = getAuthToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}${url}`, {
    headers,
    ...options,
  });
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  return res.json();
}

export const api = {
  getAnalytics: () => fetchJSON<{
    total_messages: number;
    total_users: number;
    total_facts: number;
    messages_today: number;
    misinformation_count: number;
    language_distribution: Record<string, number>;
    topic_distribution: Record<string, number>;
    daily_messages: { date: string; count: number }[];
  }>('/api/analytics'),

  getFacts: () => fetchJSON<{ facts: Array<{
    id: number; topic: string; disease: string | null;
    content_en: string; content_hausa: string | null; content_yoruba: string | null;
    content_igbo: string | null; content_french: string | null;
    source: string | null; verified_by: string | null;
    created_at: string; updated_at: string | null; is_active: number;
  }> }>('/api/facts'),

  createFact: (data: Record<string, string>) =>
    fetchJSON<{ id: number }>('/api/facts', { method: 'POST', body: JSON.stringify(data) }),

  updateFact: (id: number, data: Record<string, unknown>) =>
    fetchJSON<{ status: string }>(`/api/facts/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  deleteFact: (id: number) =>
    fetchJSON<{ status: string }>(`/api/facts/${id}`, { method: 'DELETE' }),

  getMessages: (limit = 50, offset = 0) =>
    fetchJSON<{ messages: Array<{
      id: number; user_id: number; incoming_text: string; incoming_language: string;
      detected_topic: string | null; response_text: string; phone_number: string | null;
      is_verified: number; created_at: string;
    }> }>(`/api/messages?limit=${limit}&offset=${offset}`),

  getConfig: () => fetchJSON<Record<string, string>>('/api/config'),

  updateConfig: (data: Record<string, string>) =>
    fetchJSON<{ status: string }>('/api/config', { method: 'PUT', body: JSON.stringify(data) }),

  uploadVoiceFile: (file: File, intent: string, language: string, topic: string | null, displayName: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('intent', intent);
    formData.append('language', language);
    formData.append('display_name', displayName);
    if (topic) formData.append('topic', topic);
    return fetch('/api/voice/upload', {
      method: 'POST',
      body: formData,
    }).then(res => {
      if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
      return res.json();
    });
  },

  getAppLogs: (params: { level?: string; requestId?: string; limit?: number; offset?: number } = {}) => {
    const searchParams = new URLSearchParams();
    if (params.level) searchParams.set('level', params.level);
    if (params.requestId) searchParams.set('request_id', params.requestId);
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.offset) searchParams.set('offset', String(params.offset));
    return fetchJSON<{ logs: Array<{ id: number; level: string; request_id: string; message: string; metadata: string; created_at: string }>; total: number }>(
      `/api/logs?${searchParams.toString()}`
    );
  },

  getTTSAnalytics: () =>
    fetchJSON<{
      byLanguage: Array<{ language: string; total: number; failures: number; rate: string }>;
      byProvider: Array<{ provider: string; total: number; failures: number; rate: string }>;
      recentFailures: number;
    }>('/api/analytics/tts'),

  getTemplates: (intent?: string, language?: string) => {
    const params = new URLSearchParams();
    if (intent) params.set('intent', intent);
    if (language) params.set('language', language);
    return fetchJSON<Array<{ intent: string; language: string; template_key: string; content: string }>>(
      `/api/templates?${params.toString()}`
    );
  },

  updateTemplate: (data: { intent: string; language: string; template_key: string; content: string }) =>
    fetchJSON<{ status: string }>('/api/templates', { method: 'POST', body: JSON.stringify(data) }),

  getTools: (probe = false) =>
    fetchJSON<{
      tools: ToolEntry[];
      summary: { total: number; healthy: number; degraded: number; down: number; disabled: number };
    }>(`/api/tools${probe ? '?probe=true' : ''}`),

  getTool: (id: string, probe = false) =>
    fetchJSON<{ tool: ToolEntry; usage: ToolUsage; health: ToolHealthLive | null }>(
      `/api/tools?id=${encodeURIComponent(id)}${probe ? '&probe=true' : ''}`
    ),

  getToolUsage: (id: string, range = '24') =>
    fetchJSON<{ tool_id: string; range_hours: number; usage: ToolUsage; recent_metrics: ToolMetric[]; live_probe: ToolHealthLive | null }>(
      `/api/tools/${encodeURIComponent(id)}/usage?range=${range}`
    ),

  getHealth: (deep = false) =>
    fetchJSON<Record<string, unknown>>(`/api/health${deep ? '?deep=true' : ''}`),

  getToolLogs: (params: { tool?: string; channel?: string; limit?: number; offset?: number } = {}) => {
    const searchParams = new URLSearchParams();
    if (params.tool) searchParams.set('tool', params.tool);
    if (params.channel) searchParams.set('channel', params.channel);
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.offset) searchParams.set('offset', String(params.offset));
    return fetchJSON<{ logs: ToolMetric[]; total: number }>(`/api/logs?${searchParams.toString()}`);
  },
};

export interface ToolEntry {
  id: string;
  name: string;
  category: string;
  description: string | null;
  provider: string | null;
  is_enabled: number;
  enabled: boolean;
  status: string;
  last_latency_ms: number;
  last_checked_at: string | null;
  error_details: string | null;
  usage_24h: ToolUsage;
}

export interface ToolUsage {
  total_calls: number;
  success_count: number;
  error_count: number;
  avg_latency_ms: number;
  p95_latency_ms: number;
  error_rate: number;
  by_hour: Array<{ hour: string; calls: number; errors: number; avg_latency_ms: number }>;
}

export interface ToolMetric {
  id: number;
  tool_name: string;
  channel: string;
  latency_ms: number;
  status_code: number;
  tokens_used: number;
  error_message: string | null;
  created_at: string;
}

export interface ToolHealthLive {
  toolId: string;
  status: 'healthy' | 'degraded' | 'down' | 'disabled';
  latencyMs: number;
  errorDetails?: string | null;
}
